// -----------------------------------------------------------------------------
// DashboardPage — Vue tableau de bord (stats, alertes DLC, food cost)
// StockPro / VisualisPro
// -----------------------------------------------------------------------------

import { T } from "../../i18n/translations";
import { getColors } from "../../styles/theme";
import { daysUntil } from "../../utils/date";
import { Ic, ICONS } from "../Icons";

export default function DashboardPage({
  lang,
  dark,
  accent,
  authUser,
  userProfile,
  isStaff,
  products,
  alerts,
  dlcAlerts,
  totalAlerts,
  totalValue,
  usedCats,
  fcColor,
  fcBg,
  fcLabel,
}) {
  const t = T[lang];
  const { textPrimary, textSecondary } = getColors(dark);

  return (
    <div className="slide-in">
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:800, color:textPrimary, letterSpacing:"-0.5px" }}>{t.dashboard}</h1>
        <p style={{ color:textSecondary, fontSize:12, marginTop:3 }}>
          {userProfile?.name || authUser.email} · {new Date().toLocaleDateString(lang==="ar"?"ar-DZ":"fr-BE",{weekday:"long",day:"numeric",month:"long"})}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10, marginBottom:18 }}>
        {[
          { label:t.totalProducts, val:products.length, icon:ICONS.box, color:"#FF6B35" },
          { label:t.criticalAlerts, val:totalAlerts, icon:ICONS.bell, color:"#ef4444" },
          { label:t.stockValue, val:isStaff?"—":`€${totalValue.toFixed(0)}`, icon:ICONS.chart, color:"#10b981" },
          { label:t.categories, val:usedCats.length, icon:ICONS.grid, color:"#f59e0b" },
        ].map((s,i)=>(
          <div key={i} className="card" style={{ padding:"16px 18px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <div style={{ fontSize:10, color:textSecondary, marginBottom:7, textTransform:"uppercase", letterSpacing:"1px" }}>{s.label}</div>
                <div style={{ fontSize:24, fontWeight:800, color:textPrimary, fontFamily:"'JetBrains Mono',monospace" }}>{s.val}</div>
              </div>
              <div style={{ width:34, height:34, background:`${s.color}18`, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <Ic d={s.icon} size={16} color={s.color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* DLC Alerts */}
      {dlcAlerts.length>0 && (
        <div className="card" style={{ padding:"16px 18px", marginBottom:14, borderColor:"rgba(245,158,11,.3)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:12 }}>
            <Ic d={ICONS.warn} size={15} color="#f59e0b" />
            <span style={{ fontWeight:700, color:"#fbbf24", fontSize:13 }}>DLC — Dates d'expiration proches</span>
          </div>
          {dlcAlerts.map(p=>{
            const d=daysUntil(p.dlc);
            return(
              <div key={p.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 11px", background:"rgba(245,158,11,.06)", borderRadius:8, border:"1px solid rgba(245,158,11,.15)", marginBottom:5 }}>
                <span style={{ fontWeight:600, color:textPrimary, fontSize:13 }}>{p.name}</span>
                <span className="tag" style={{ background:d<=0?"rgba(239,68,68,.15)":"rgba(245,158,11,.15)", color:d<=0?"#f87171":"#fbbf24" }}>
                  {d<=0?t.dlcExpired:`${t.dlcSoon} (${d}j)`}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Stock Alerts */}
      {alerts.length>0 && (
        <div className="card" style={{ padding:"16px 18px", marginBottom:14, borderColor:"rgba(239,68,68,.2)" }}>
          <div style={{ fontWeight:700, color:"#f87171", fontSize:13, marginBottom:10, display:"flex", alignItems:"center", gap:7 }}>
            <Ic d={ICONS.warn} size={15} color="#ef4444" /> {t.criticalAlerts}
          </div>
          {alerts.slice(0,4).map(p=>(
            <div key={p.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"7px 10px", background:"rgba(239,68,68,.05)", borderRadius:7, marginBottom:4 }}>
              <span style={{ fontSize:13, color:textPrimary, fontWeight:600 }}>{p.name}</span>
              <span style={{ fontFamily:"'JetBrains Mono',monospace", color:"#f87171", fontWeight:700, fontSize:13 }}>{p.qty} {p.unit}</span>
            </div>
          ))}
        </div>
      )}

      {/* Category Chart */}
      <div className="card" style={{ padding:"16px 18px" }}>
        <div style={{ fontWeight:700, color:textPrimary, marginBottom:12, fontSize:13 }}>Répartition par catégorie</div>
        {usedCats.length===0 ? (
          <p style={{ color:textSecondary, fontSize:13 }}>Aucun produit encore ajouté.</p>
        ) : usedCats.map(cat=>{
          const ps=products.filter(p=>p.cat===cat);
          const val=ps.reduce((s,p)=>s+(p.qty||0)*(p.price||0),0);
          const pct=Math.round(ps.length/products.length*100);
          return(
            <div key={cat} style={{ marginBottom:9 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontSize:12, color:textSecondary }}>{cat}</span>
                <div style={{ display:"flex", gap:10 }}>
                  <span style={{ fontSize:11, color:textSecondary }}>{ps.length} produit{ps.length>1?"s":""}</span>
                  <span style={{ fontSize:11, color:"#10b981", fontFamily:"'JetBrains Mono',monospace" }}>€{val.toFixed(0)}</span>
                </div>
              </div>
              <div style={{ height:4, background:dark?"rgba(255,255,255,.05)":"rgba(0,0,0,.06)", borderRadius:2 }}>
                <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${accent},#f7931e)`, borderRadius:2, transition:".4s" }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Food Cost Card — admin only */}
      {!isStaff && (() => {
        const withFC = products.filter(p => p.sellPrice > 0 && p.price > 0);
        if (!withFC.length) return null;
        const fcs = withFC.map(p => ({ name:p.name, fc: Math.round((p.price/p.sellPrice)*100) }));
        const avg = Math.round(fcs.reduce((s,p)=>s+p.fc,0)/fcs.length);
        const best = fcs.reduce((a,b)=>a.fc<b.fc?a:b);
        const worst = fcs.reduce((a,b)=>a.fc>b.fc?a:b);
        return (
          <div className="card" style={{ padding:"16px 18px", marginTop:14 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
              <div style={{ fontWeight:700, color:textPrimary, fontSize:13 }}>{t.foodCostTitle}</div>
              <span style={{ fontSize:11, color:textSecondary }}>{withFC.length} produit{withFC.length>1?"s":""} configuré{withFC.length>1?"s":""}</span>
            </div>
            {/* Avg food cost */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 12px", borderRadius:10, background:fcBg(avg), marginBottom:10 }}>
              <span style={{ fontSize:13, color:textPrimary, fontWeight:600 }}>{t.avgFoodCost}</span>
              <span style={{ fontSize:20, fontWeight:800, color:fcColor(avg), fontFamily:"'JetBrains Mono',monospace" }}>{fcLabel(avg)} {avg}%</span>
            </div>
            {/* Best / Worst */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              <div style={{ padding:"10px 12px", borderRadius:10, background:"rgba(16,185,129,.08)", border:"1px solid rgba(16,185,129,.15)" }}>
                <div style={{ fontSize:10, color:textSecondary, marginBottom:4, textTransform:"uppercase", letterSpacing:"1px" }}>{t.bestMargin}</div>
                <div style={{ fontSize:13, fontWeight:700, color:"#10b981", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{best.name}</div>
                <div style={{ fontSize:12, color:"#10b981", fontFamily:"'JetBrains Mono',monospace" }}>FC {best.fc}%</div>
              </div>
              <div style={{ padding:"10px 12px", borderRadius:10, background:"rgba(239,68,68,.08)", border:"1px solid rgba(239,68,68,.15)" }}>
                <div style={{ fontSize:10, color:textSecondary, marginBottom:4, textTransform:"uppercase", letterSpacing:"1px" }}>{t.worstMargin}</div>
                <div style={{ fontSize:13, fontWeight:700, color:"#f87171", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{worst.name}</div>
                <div style={{ fontSize:12, color:"#f87171", fontFamily:"'JetBrains Mono',monospace" }}>FC {worst.fc}%</div>
              </div>
            </div>
            {/* Per product bars */}
            <div style={{ marginTop:12 }}>
              {fcs.map((p,i)=>(
                <div key={i} style={{ marginBottom:7 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                    <span style={{ fontSize:11, color:textSecondary }}>{p.name}</span>
                    <span style={{ fontSize:11, fontWeight:700, color:fcColor(p.fc), fontFamily:"'JetBrains Mono',monospace" }}>{p.fc}%</span>
                  </div>
                  <div style={{ height:4, background:dark?"rgba(255,255,255,.05)":"rgba(0,0,0,.06)", borderRadius:2 }}>
                    <div style={{ height:"100%", width:`${Math.min(100,p.fc)}%`, background:fcColor(p.fc), borderRadius:2, transition:".4s" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
